import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Box,
  Checkbox,
  Typography,
  Button,
  Stack,
  Tooltip,
  Paper,
  alpha,
  Collapse,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Snackbar,
  Alert,
} from "@mui/material";
import {
  formatAmount,
  formatHistoryAmount,
  getCommaOut,
} from "../../utils/hooks/dataHandler";
import ReorderableTable from "./DndTable";
import DeleteIcon from "@mui/icons-material/Delete";
import { TextFieldForName } from "../utils/TextField/TextField";
import { CalculatorTextField } from "../utils/TextField/NumericTextField";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { StyledIconButton } from "../utils/Icon";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import CustomDialog from "../utils/dialog/customDialog";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ActionMenu from "./ActionMenu";
import { v4 as uuidv4 } from "uuid";
import { useSuccessDialog } from "../../contexts/SuccessDialogContext";
import { useErrorDialog } from "../../contexts/ErrorDialogContext";
import {
  LinkedRowSyncBanner,
  ScheduleTaggingDialog,
  collectDirtyLinkedSources,
  markRowSynced,
  markRowsSynced,
  useScheduleTagging,
} from "../Common/TaxAdjustment";
import AdjustmentTaggingSelect, {
  createValidationRules,
} from "../utils/AdjustmentTaggingSelect";
import ChangeTaggingDialog from "../Common/ChangeTaggingDialog";
import RemoveTaggingDialog from "../Common/RemoveTaggingDialog";
import ExpandedArrowBox from "../Common/ExpandedArrowBox";
import AssignmentIcon from "@mui/icons-material/Assignment";
import {
  getDetailPlTableDisplayConfig,
  getViewportWidth,
  shouldUseCompactDetailPlTable,
} from "../../utils/responsiveLayoutSettings";

const areRowValuesEqual = (left, right) => {
  if (Array.isArray(left) || Array.isArray(right)) {
    if (!Array.isArray(left) || !Array.isArray(right)) return false;
    if (left.length !== right.length) return false;
    return left.every((item, index) => Object.is(item, right[index]));
  }

  return Object.is(left, right);
};

const hasRowChanged = (currentRow, nextRow) => {
  const keys = new Set([
    ...Object.keys(currentRow || {}),
    ...Object.keys(nextRow || {}),
  ]);

  return [...keys].some(
    (key) => !areRowValuesEqual(currentRow?.[key], nextRow?.[key])
  );
};

const Details = ({
  company,
  detailsId,
  tcId,
  revenue,
  otherIncome,
  costOfSales,
  expenses,
  getSectionData,
  updateSectionRows,
  updateSectionSign,
  handleViewSchedulePage,
  onDoneTagging,
  sections,
  sectionTotals,
  flipSignMode,
  canEditDetailPL,
  handleSave,
}) => {
  const { unTagSchedule, updateLinkedRow, updateLinkedRows } = useScheduleTagging();
  const handleSuccess = useSuccessDialog();
  const handleError = useErrorDialog();
  const [useCompactTable, setUseCompactTable] = useState(() =>
    shouldUseCompactDetailPlTable()
  );
  const [displayViewportWidth, setDisplayViewportWidth] = useState(() =>
    getViewportWidth()
  );
  const detailPlTableDisplayConfig = React.useMemo(
    () => getDetailPlTableDisplayConfig(useCompactTable, displayViewportWidth),
    [useCompactTable, displayViewportWidth]
  );
  const bottomActionBarConfig = detailPlTableDisplayConfig.bottomActionBar;
  const emptyStateConfig = detailPlTableDisplayConfig.emptyState;
  const sectionHeaderConfig = detailPlTableDisplayConfig.sectionHeader;

  const [selectedRows, setSelectedRows] = useState({
    Revenue: [],
    "Other Income": [],
    "Cost of Sales": [],
    Expenses: [],
  });

  const [expandedSections, setExpandedSections] = useState(
    sections.reduce((acc, section) => ({ ...acc, [section]: true }), {})
  );
  const [expandedRows, setExpandedRows] = useState({});
  const [newRowFocus, setNewRowFocus] = useState(null);

  const toggleSectionExpansion = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const toggleRowExpansion = (section, index) => {
    setExpandedRows((prev) => ({
      ...prev,
      [`${section}-${index}`]: !prev[`${section}-${index}`],
    }));
  };

  const [removeSelectedDialog, setRemoveSelectedDialog] = useState({
    open: false,
    section: null,
  });
  const [removeSelectedLoading, setRemoveSelectedLoading] = useState(false);
  const [adjustmentDialog, setAdjustmentDialog] = useState({
    tagging: false,
    remove: false,
    row: null,
    section: null,
    index: null,
    value: null,
  });

  const [mergeDialog, setMergeDialog] = useState({
    open: false,
    section: null,
  });

  // Menu state for action dropdown
  const [actionMenu, setActionMenu] = useState({
    anchorEl: null,
    row: null,
    section: null,
    index: null,
  });
  const [selectedActionMenu, setSelectedActionMenu] = useState({
    anchorEl: null,
    section: null,
  });

  const [taggingDialog, setTaggingDialog] = useState(false);

  // Snackbar state for copy feedback
  const [copySnackbar, setCopySnackbar] = useState({
    open: false,
    message: "",
  });

  useEffect(() => {
    const updateTableDisplayMode = () => {
      setUseCompactTable(shouldUseCompactDetailPlTable());
      setDisplayViewportWidth(getViewportWidth());
    };

    updateTableDisplayMode();
    window.addEventListener("resize", updateTableDisplayMode);
    window.visualViewport?.addEventListener("resize", updateTableDisplayMode);

    return () => {
      window.removeEventListener("resize", updateTableDisplayMode);
      window.visualViewport?.removeEventListener(
        "resize",
        updateTableDisplayMode
      );
    };
  }, []);

  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") return;
    setCopySnackbar({ open: false, message: "" });
  };

  // Helper to copy row name & amount to clipboard with snackbar feedback
  const copyRowToClipboard = useCallback(
    (row) => {
      const textToCopy = `${row.name} - ${formatAmount(row.amount)}`;
      navigator.clipboard
        .writeText(textToCopy)
        .then(() => {
          setCopySnackbar({
            open: true,
            message: `Copied: ${textToCopy}`,
          });
        })
        .catch(() => {
          handleError("Failed to copy to clipboard.");
        });
    },
    [handleError]
  );

  // Helper to copy multiple selected rows to clipboard
  const copySelectedRowsToClipboard = useCallback(
    (section) => {
      const currentRows = getSectionData(section);
      const selected = selectedRows[section] || [];
      if (selected.length === 0) return;

      const rowsToCopy = currentRows.filter((row) =>
        selected.includes(row.row_id)
      );
      const textToCopy = rowsToCopy
        .map((row) => `${row.name} - ${formatAmount(row.amount)}`)
        .join("\n");

      navigator.clipboard
        .writeText(textToCopy)
        .then(() => {
          setCopySnackbar({
            open: true,
            message: `Copied ${rowsToCopy.length} row(s) to clipboard`,
          });
        })
        .catch(() => {
          handleError("Failed to copy to clipboard.");
        });
    },
    [selectedRows, handleError, getSectionData]
  );

  // Keyboard shortcut handler for Ctrl+Shift+C on name/amount fields
  const handleCopyKeyDown = useCallback(
    (e, row) => {
      if (e.ctrlKey && e.shiftKey && (e.key === "c" || e.key === "C")) {
        e.preventDefault();
        copyRowToClipboard(row);
      }
    },
    [copyRowToClipboard]
  );

  const updateSectionRowById = useCallback(
    (section, rowId, rowUpdater) => {
      if (!rowId) return false;

      const currentRows = getSectionData(section) || [];
      const rowIndex = currentRows.findIndex((row) => row.row_id === rowId);
      if (rowIndex === -1) return false;

      const currentRow = currentRows[rowIndex];
      const nextRow =
        typeof rowUpdater === "function"
          ? rowUpdater(currentRow)
          : { ...currentRow, ...rowUpdater };

      if (!nextRow || !hasRowChanged(currentRow, nextRow)) {
        return false;
      }

      const updatedRows = [...currentRows];
      updatedRows[rowIndex] = nextRow;
      updateSectionRows(section, updatedRows);

      return true;
    },
    [getSectionData, updateSectionRows]
  );

  const onCloseTaggingDialog = () => {
    setTaggingDialog({
      open: false,
      row: null,
      section: null,
      index: null,
    });
  };

  const removeRow = (section, index) => {
    const currentRows = getSectionData(section);
    const updatedRows = currentRows.filter((_, i) => i !== index);

    updateSectionRows(section, updatedRows);
    setAdjustmentDialog((prev) => ({ ...prev, remove: false }));

    return updatedRows;
  };

  const handleRowRemove = (row, section, index) => {
    if (row.schedule)
      setAdjustmentDialog({
        tagging: false,
        remove: true,
        row,
        section,
        index,
      });
    else {
      removeRow(section, index);
    }
  };

  // Menu handlers
  const handleActionMenuOpen = (event, row, section, index) => {
    setActionMenu({
      anchorEl: event.currentTarget,
      row,
      section,
      index,
    });
  };

  const handleActionMenuClose = () => {
    setActionMenu({
      anchorEl: null,
      row: null,
      section: null,
      index: null,
    });
  };

  const handleActionClick = (action) => {
    const { row, section, index } = actionMenu;
    switch (action) {
      case "tag":
        setTaggingDialog({
          open: true,
          isEdit: false,
          row: row,
          section: section,
          index: index,
        });
        break;
      case "view":
        handleViewSchedulePage(section, index, row.taxAdjustment);
        break;
      case "edit":
        setTaggingDialog({
          open: true,
          isEdit: true,
          row: row,
          section: section,
          index: index,
        });
        break;
      case "summary":
        toggleRowExpansion(section, index);
        break;
      case "unmerge":
        handleUnmergeRow(section, index);
        break;
      case "copy":
        copyRowToClipboard(row);
        break;
      case "delete":
        handleRowRemove(row, section, index);
        break;
      default:
        break;
    }

    handleActionMenuClose();
  };

  const handleSelectedActionMenuOpen = (event, section) => {
    event.stopPropagation();
    setSelectedActionMenu({
      anchorEl: event.currentTarget,
      section,
    });
  };

  const handleSelectedActionMenuClose = () => {
    setSelectedActionMenu({
      anchorEl: null,
      section: null,
    });
  };

  const handleUpdatingTaxAdjustment = (value, row, section, index) => {
    if (
      !row.taxAdjustment ||
      row.taxAdjustment === "" ||
      ["", "Added Back", "Deduct"].includes(row.taxAdjustment) ||
      !row.schedule
    ) {
      updateSectionRowById(section, row.row_id, (currentRow) => ({
        ...currentRow,
        taxAdjustment: value,
      }));

      setAdjustmentDialog({
        tagging: false,
        remove: false,
        row: null,
        section: null,
        index: null,
        value: null,
      });
    } else
      setAdjustmentDialog({
        tagging: true,
        remove: false,
        row,
        section,
        index,
        value,
      });
  };

  // Add a new blank row to a section
  const handleAddRow = React.useCallback(
    (section) => {
      const rowId = uuidv4();
      const newRow = {
        row_id: rowId,
        name: "",
        amount: 0,
        historyAmount: [],
        taxAdjustment: "",
        history: [],
        accountCode: "",
        reference: "",
      };
      const currentRows = getSectionData(section);
      updateSectionRows(section, [...currentRows, newRow]);
      setExpandedSections((prev) => ({ ...prev, [section]: true }));
      setNewRowFocus({ section, rowId });
    },
    [getSectionData, updateSectionRows]
  );

  useEffect(() => {
    if (!newRowFocus) return;

    const frameId = requestAnimationFrame(() => {
      const rowElement = document.querySelector(
        `[data-detail-pl-row-id="${newRowFocus.rowId}"]`
      );

      rowElement?.scrollIntoView({
        block: "center",
        behavior: "smooth",
      });

      setNewRowFocus(null);
    });

    return () => cancelAnimationFrame(frameId);
  }, [newRowFocus, revenue, otherIncome, costOfSales, expenses]);

  // Toggle row selection for a section
  const handleRowSelect = React.useCallback((section, row_id) => {
    setSelectedRows((prev) => {
      const current = prev[section] || [];
      const exists = current.includes(row_id);
      return {
        ...prev,
        [section]: exists
          ? current.filter((id) => id !== row_id)
          : [...current, row_id],
      };
    });
  }, []);

  const handleRemoveSelectedRows = React.useCallback(
    async (section) => {
      setRemoveSelectedLoading(true);
      const currentRows = getSectionData(section);
      const selected = selectedRows[section] || [];

      try {
        await Promise.all(
          selected.map(async (rowId) => {
            const row = currentRows.find((r) => r.row_id === rowId);
            if (row?.schedule) {
              await unTagSchedule(
                "p_l",
                row.row_id,
                row.schedule.scheduleType === "FAAdditions"
                  ? "ca_schedule"
                  : "pl_schedule",
                false
              );
            }
          })
        );

        const newRows = currentRows.filter(
          (row) => !selected.includes(row.row_id)
        );
        updateSectionRows(section, newRows);
        setSelectedRows((prev) => ({ ...prev, [section]: [] }));
        setRemoveSelectedDialog({ open: false, section: null });
        await handleSave("Successfully removed the selected rows.", false, {
          section,
          updatedRows: newRows,
        });
      } catch (error) {
        handleError(error);
      } finally {
        setRemoveSelectedLoading(false);
      }
    },
    [
      selectedRows,
      getSectionData,
      updateSectionRows,
      unTagSchedule,
      handleSave,
      handleError,
    ]
  );

  // Select all rows in a section
  const handleSelectAll = React.useCallback(
    (section) => {
      const currentRows = getSectionData(section);
      const allIds = currentRows.map((row) => row.row_id);
      const allSelected =
        selectedRows[section]?.length === allIds.length && allIds.length > 0;
      setSelectedRows((prev) => ({
        ...prev,
        [section]: allSelected ? [] : allIds,
      }));
    },
    [selectedRows, getSectionData]
  );

  // Returns true if all selected rows in a section have the same taxAdjustment or are all ""
  const areSelectedRowsMergeable = (section) => {
    const currentRows = getSectionData(section);
    const selected = selectedRows[section] || [];
    if (selected.length < 2) return false;

    const selectedTaxAdjustments = selected
      .map(
        (rowId) =>
          currentRows.find((r) => r.row_id === rowId)?.taxAdjustment ?? ""
      )
      .filter((v) => v !== undefined);

    // Only allow merge if all are "" (None)
    return selectedTaxAdjustments.every((v) => v === "");
  };

  const handleMergeSelectedRows = (section) => {
    const selectedIndexes = selectedRows[section].sort((a, b) => a - b);
    if (selectedIndexes.length < 2 || !areSelectedRowsMergeable(section)) {
      handleError("Please make sure you have selected valid rows to merge");
      return;
    }
    setMergeDialog({ open: true, section });
  };

  const handleSelectedActionClick = (action) => {
    const section = selectedActionMenu.section;
    if (!section) return;

    switch (action) {
      case "copy":
        copySelectedRowsToClipboard(section);
        break;
      case "merge":
        handleMergeSelectedRows(section);
        break;
      case "remove":
        setRemoveSelectedDialog({ open: true, section });
        break;
      default:
        break;
    }

    handleSelectedActionMenuClose();
  };

  const handleUnmergeRow = (section, index) => {
    const row = getSectionData(section)[index];

    if (row.taxAdjustment !== "") {
      handleError(
        "You need to clear the tax adjustment first before you can unmerge it."
      );
      return;
    }

    // Check if this is a merged row (has history with multiple entries)
    if (!row.history || row.history.length <= 1) {
      return;
    }

    // Create new rows from history
    const newRows = row.history.map((historyItem) => ({
      row_id: uuidv4(),
      name: historyItem.name,
      amount: historyItem.amount,
      historyAmount: Array.isArray(historyItem.amount)
        ? historyItem.amount
        : [historyItem.amount],
      taxAdjustment: "",
      history: [],
      accountCode: historyItem.accountCode || "",
      reference: historyItem.reference,
    }));

    // Remove the merged row and add the unmerged rows
    const updatedRows = [
      ...getSectionData(section).slice(0, index),
      ...newRows,
      ...getSectionData(section).slice(index + 1),
    ];

    updateSectionRows(section, updatedRows);

    setExpandedRows((prev) => ({
      ...prev,
      [`${section}-${index}`]: !prev[`${section}-${index}`],
    }));
  };

  const handleConfirmMerge = () => {
    const section = mergeDialog.section;
    mergeRows(section, selectedRows[section]);

    setSelectedRows({
      ...selectedRows,
      [section]: [],
    });

    setMergeDialog({ open: false, section: null });
  };

  const mergeRows = (section, rowsToMerge) => {
    const sectionRows = getSectionData(section);

    const rows = sectionRows.filter((row) => rowsToMerge.includes(row.row_id));

    if (rows.length === 0) return;

    const targetRow = rows[0];

    const mergedHistoryAmount = rows.flatMap(
      (row) => row.historyAmount || [parseFloat(getCommaOut(row.amount))]
    );

    const newSumAmount = mergedHistoryAmount.reduce((sum, num) => sum + num, 0);

    const mergedHistory = rows.flatMap((row) =>
      row.history?.length > 0
        ? row.history
        : [
            {
              row_id: row.row_id,
              name: row.name,
              amount: row.historyAmount,
              accountCode: row.accountCode,
              reference: row.reference,
            },
          ]
    );

    const newRow = {
      ...targetRow,
      row_id: uuidv4(),
      name: rows.map((row) => row.name).join(" & "),
      amount: newSumAmount.toString(),
      historyAmount: mergedHistoryAmount,
      history: mergedHistory,
      reference: "",
    };

    const updatedRows = sectionRows
      .filter((row) => !rowsToMerge.includes(row.row_id))
      .concat(newRow);

    updateSectionRows(section, updatedRows);
  };

  const selectedActionSection = selectedActionMenu.section;
  const selectedActionCount = selectedRows[selectedActionSection]?.length || 0;
  const selectedActionMergeDisabled =
    !selectedActionSection ||
    selectedActionCount < 2 ||
    !areSelectedRowsMergeable(selectedActionSection);
  const taxAdjustmentColumnConfig =
    detailPlTableDisplayConfig.taxAdjustmentColumn;

  const dirtyLinkedSources = useMemo(
    () =>
      collectDirtyLinkedSources(
        {
          Revenue: revenue,
          "Other Income": otherIncome,
          "Cost of Sales": costOfSales,
          Expenses: expenses,
        },
        { sourceType: "p_l", sourceId: detailsId }
      ),
    [revenue, otherIncome, costOfSales, expenses, detailsId]
  );
  const [syncingLinkedRows, setSyncingLinkedRows] = useState(false);

  const persistSyncedRows = useCallback(
    async (rowIds, successMessage) => {
      const ids = new Set(rowIds);
      const nextSections = {
        Revenue: markRowsSynced(revenue, ids),
        "Other Income": markRowsSynced(otherIncome, ids),
        "Cost of Sales": markRowsSynced(costOfSales, ids),
        Expenses: markRowsSynced(expenses, ids),
      };
      Object.entries(nextSections).forEach(([section, rows]) => {
        updateSectionRows(section, rows);
      });
      await handleSave(successMessage, true, { sections: nextSections });
    },
    [revenue, otherIncome, costOfSales, expenses, updateSectionRows, handleSave]
  );

  const handleSyncLinkedRow = useCallback(
    async (source) => {
      await updateLinkedRow(source, { showMessage: false });
      const currentRows = getSectionData(source.section) || [];
      const currentRow =
        currentRows.find((row) => row.row_id === source.row?.row_id) || source.row;
      const updatedRows = currentRows.map((row) =>
        row.row_id === currentRow.row_id ? markRowSynced(currentRow) : row
      );
      updateSectionRows(source.section, updatedRows);
      await handleSave("Linked schedule updated and saved.", true, {
        section: source.section,
        updatedRows,
      });
    },
    [updateLinkedRow, getSectionData, updateSectionRows, handleSave]
  );

  const handleSyncAllLinkedRows = useCallback(async () => {
    if (!dirtyLinkedSources.length) return;
    setSyncingLinkedRows(true);
    try {
      await updateLinkedRows(dirtyLinkedSources, { showMessage: false });
      await persistSyncedRows(
        dirtyLinkedSources.map((source) => source.row.row_id),
        dirtyLinkedSources.length === 1
          ? "Linked schedule updated and saved."
          : `Linked schedules updated and saved for ${dirtyLinkedSources.length} rows.`
      );
    } finally {
      setSyncingLinkedRows(false);
    }
  }, [dirtyLinkedSources, updateLinkedRows, persistSyncedRows]);

  return (
    <Stack spacing={3}>
      <LinkedRowSyncBanner
        count={dirtyLinkedSources.length}
        onSyncAll={handleSyncAllLinkedRows}
        syncing={syncingLinkedRows}
        canEdit={canEditDetailPL}
        sourceLabel="Detailed P&L"
      />
      {sections.map((section) => (
        <Paper
          key={section}
          elevation={0}
          sx={{
            borderRadius: 3,
            border: "1px solid",
            borderColor: "divider",
            overflow: "hidden",
            transition: "all 0.2s ease-in-out",
            "&:hover": {
              boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.08)",
            },
          }}
        >
          {/* Section Header */}
          <Stack
            direction='row'
            alignItems='center'
            spacing={sectionHeaderConfig.spacing}
            sx={{
              px: sectionHeaderConfig.px,
              py: sectionHeaderConfig.py,
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.03),
              borderBottom: "1px solid",
              borderColor: "divider",
              cursor: "pointer",
            }}
            onClick={() => toggleSectionExpansion(section)}
          >
            <Box
              sx={{
                width: sectionHeaderConfig.iconSize,
                height: sectionHeaderConfig.iconSize,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: 2,
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
              }}
            >
              <ReceiptLongIcon color='primary' />
            </Box>
            <Box
              flex={1}
              sx={{
                alignItems: useCompactTable ? "baseline" : "flex-start",
                display: useCompactTable ? "flex" : "block",
                gap: 1.25,
                minWidth: 0,
              }}
            >
              <Typography
                variant={sectionHeaderConfig.titleVariant}
                fontWeight={600}
                color='text.primary'
                noWrap={useCompactTable}
              >
                {section}
              </Typography>
              <Typography
                variant={useCompactTable ? "caption" : "body2"}
                color='text.secondary'
                noWrap={useCompactTable}
              >
                {getSectionData(section)?.length || 0} items
              </Typography>
            </Box>
            <Stack
              direction='row'
              spacing={useCompactTable ? 1 : 2}
              alignItems='center'
              sx={{ flexShrink: 0 }}
            >
              <Typography
                variant={useCompactTable ? "body1" : "h6"}
                fontWeight={600}
                color={(theme) =>
                  sectionTotals[section] < 0
                    ? theme.palette.error.main
                    : theme.palette.success.main
                }
              >
                {formatAmount(sectionTotals[section])}
              </Typography>
              {canEditDetailPL && (
                <Tooltip
                  title={`Flip sign to ${
                    flipSignMode[section] === "positive"
                      ? "negative"
                      : "positive"
                  }`}
                >
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      updateSectionSign(section);
                    }}
                    size='small'
                    sx={useCompactTable ? { height: 32, width: 32 } : undefined}
                  >
                    <CompareArrowsIcon />
                  </IconButton>
                </Tooltip>
              )}
              {canEditDetailPL && (
                <Tooltip title='Add row'>
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAddRow(section);
                    }}
                    size='small'
                    color='primary'
                    sx={{
                      bgcolor: (theme) =>
                        alpha(theme.palette.primary.main, 0.08),
                      height: useCompactTable ? 32 : undefined,
                      width: useCompactTable ? 32 : undefined,
                      "&:hover": {
                        bgcolor: (theme) =>
                          alpha(theme.palette.primary.main, 0.14),
                      },
                    }}
                  >
                    <AddCircleOutlineIcon fontSize='small' />
                  </IconButton>
                </Tooltip>
              )}

              <ExpandedArrowBox expanded={expandedSections[section]} />
            </Stack>
          </Stack>

          {/* Section Content */}
          <Collapse in={expandedSections[section]}>
            <Box>
              {getSectionData(section) && getSectionData(section).length > 0 ? (
                <>
                  <ReorderableTable
                    rows={getSectionData(section)}
                    rowKey='row_id'
                    section={section}
                    setRows={(rows) => updateSectionRows(section, rows)}
                    expandedRows={expandedRows}
                    onToggleRowExpansion={toggleRowExpansion}
                    canReorder={canEditDetailPL}
                    maxHeight={detailPlTableDisplayConfig.tableMaxHeight}
                    tableDisplayConfig={detailPlTableDisplayConfig}
                    columns={[
                      ...(canEditDetailPL
                        ? [
                            {
                              key: "selected",
                              label: (
                                <Checkbox
                                  checked={
                                    getSectionData(section).length > 0 &&
                                    selectedRows[section]?.length ===
                                      getSectionData(section).length
                                  }
                                  indeterminate={
                                    selectedRows[section]?.length > 0 &&
                                    selectedRows[section]?.length <
                                      getSectionData(section).length
                                  }
                                  onChange={() => handleSelectAll(section)}
                                  inputProps={{
                                    "aria-label": "select all rows",
                                  }}
                                />
                              ),
                              headerAlign: "center",
                              width:
                                detailPlTableDisplayConfig.checkboxColumnWidth,
                              fixedSize: true,
                              render: (row, index) => (
                                <Checkbox
                                  checked={
                                    selectedRows[section]?.includes(
                                      row.row_id
                                    ) || false
                                  }
                                  onChange={() =>
                                    handleRowSelect(section, row.row_id)
                                  }
                                />
                              ),
                            },
                          ]
                        : []),
                      {
                        key: "name",
                        label: "Name",
                        headerAlign: "center",
                        width: detailPlTableDisplayConfig.columns.name,
                        render: (row, index) => (
                          <Box onKeyDown={(e) => handleCopyKeyDown(e, row)}>
                            <TextFieldForName
                              value={row.name}
                              onChange={(value) => {
                                updateSectionRowById(
                                  section,
                                  row.row_id,
                                  (currentRow) => ({
                                    ...currentRow,
                                    name: value,
                                  })
                                );
                              }}
                              reference={{
                                index: index,
                                rowId: row.row_id,
                                field: "name",
                                section: section,
                              }}
                              charLimit={60}
                              autoFocus={
                                newRowFocus?.section === section &&
                                newRowFocus?.rowId === row.row_id
                              }
                              disabled={!canEditDetailPL}
                              canEdit={canEditDetailPL}
                            />
                          </Box>
                        ),
                      },
                      {
                        key: "taxAdjustment",
                        label: "Tax Adjustment",
                        headerAlign: "center",
                        width: detailPlTableDisplayConfig.columns.taxAdjustment,
                        fixedSize: true,
                        render: (row, index) => {
                          return (
                            <Box sx={{ width: "100%", minWidth: 0 }}>
                              <AdjustmentTaggingSelect
                                linkedRowSource={{
                                  row,
                                  sourceType: "p_l",
                                  sourceId: detailsId,
                                  section,
                                  rowIndex: index,
                                }}
                                onSyncLinkedRow={handleSyncLinkedRow}
                                enableSearch
                                value={row.taxAdjustment}
                                onOpenTaggingDialog={() =>
                                  setTaggingDialog({
                                    open: true,
                                    isEdit: false,
                                    row: row,
                                    section: section,
                                    index: index,
                                  })
                                }
                                onChange={(e) => {
                                  handleUpdatingTaxAdjustment(
                                    e.target.value,
                                    row,
                                    section,
                                    index
                                  );
                                }}
                                company={company}
                                placeholder='Select tax adjustment'
                                fullWidth
                                validationRules={[
                                  createValidationRules.custom(
                                    (value, context) => ({
                                      isValid: ![
                                        "",
                                        "Added Back",
                                        "Deduct",
                                      ].includes(context.taxAdjustment)
                                        ? Boolean(context.schedule)
                                        : true,
                                    }),
                                    "This row needs to be tagged to a schedule. Click to tag."
                                  ),
                                ]}
                                validationContext={row}
                                showValidationIcon={true}
                                validationIconSlotWidth={
                                  taxAdjustmentColumnConfig.iconSlotWidth
                                }
                                sx={{
                                  minWidth: 0,
                                  width: "100%",
                                  "& .MuiSelect-select": {
                                    fontWeight: 400,
                                    minHeight: 18,
                                    overflow: "hidden",
                                    pl: 1,
                                    pr: "26px !important",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  },
                                  "& .MuiSelect-icon": {
                                    fontSize: "1rem",
                                    right: 6,
                                  },
                                }}
                                canEdit={canEditDetailPL}
                              />
                            </Box>
                          );
                        },
                      },
                      {
                        key: "amount",
                        label: "Amount",
                        headerAlign: "center",
                        width: detailPlTableDisplayConfig.columns.amount,
                        render: (row, index) => (
                          <Tooltip
                            title={
                              row.historyAmount &&
                              row.historyAmount.length > 1 &&
                              formatHistoryAmount(row.historyAmount)
                            }
                            placement='top'
                          >
                            <Box
                              display='inline-block'
                              onKeyDown={(e) => handleCopyKeyDown(e, row)}
                            >
                              <CalculatorTextField
                                value={row.historyAmount || []}
                                onChange={(value) => {
                                  // When historyAmount changes, also update amount
                                  updateSectionRowById(
                                    section,
                                    row.row_id,
                                    (currentRow) => ({
                                      ...currentRow,
                                      historyAmount: value,
                                      amount: value.reduce(
                                        (sum, num) => sum + num,
                                        0
                                      ),
                                    })
                                  );
                                }}
                                reference={{
                                  index: index,
                                  rowId: row.row_id,
                                  field: "historyAmount",
                                  section: section,
                                }}
                                disabled={!canEditDetailPL}
                                canEdit={canEditDetailPL}
                              />
                            </Box>
                          </Tooltip>
                        ),
                      },
                      {
                        key: "reference",
                        label: "Reference",
                        headerAlign: "center",
                        width: detailPlTableDisplayConfig.columns.reference,
                        render: (row, index) => (
                          <Box onKeyDown={(e) => handleCopyKeyDown(e, row)}>
                            <TextFieldForName
                              value={row.reference}
                              onChange={(value) => {
                                updateSectionRowById(
                                  section,
                                  row.row_id,
                                  (currentRow) => ({
                                    ...currentRow,
                                    reference: value,
                                  })
                                );
                              }}
                              reference={{
                                index: index,
                                rowId: row.row_id,
                                field: "reference",
                                section: section,
                              }}
                              charLimit={60}
                              disabled={!canEditDetailPL}
                              canEdit={canEditDetailPL}
                              viewOnlyAlign='center'
                            />
                          </Box>
                        ),
                      },
                      {
                        key: "action",
                        label: "Actions",
                        headerAlign: "center",
                        colAlign: "center",
                        width: detailPlTableDisplayConfig.actionColumnWidth,
                        fixedSize: useCompactTable,
                        render: (row, index) => {
                          const hasScheduleActions =
                            !["", "Added Back", "Deduct"].includes(
                              row.taxAdjustment
                            ) || row.schedule;
                          const hasMergeActions =
                            canEditDetailPL &&
                            row.history &&
                            row.history.length > 0;
                          const showVertMenu =
                            hasScheduleActions || hasMergeActions;

                          if (useCompactTable) {
                            return (
                              <StyledIconButton
                                onClick={(event) =>
                                  handleActionMenuOpen(
                                    event,
                                    row,
                                    section,
                                    index
                                  )
                                }
                                icon={MoreVertIcon}
                                iconColor='text.secondary'
                                sx={{
                                  p: 0.5,
                                  "&:hover": {
                                    backgroundColor: (theme) =>
                                      alpha(theme.palette.text.secondary, 0.1),
                                  },
                                }}
                              />
                            );
                          }

                          return (
                            <Stack
                              direction='row'
                              spacing={0.5}
                              justifyContent='center'
                              alignItems='center'
                            >
                              <StyledIconButton
                                onClick={() => copyRowToClipboard(row)}
                                icon={AssignmentIcon}
                                tooltip='Copy Name & Amount • Ctrl+Shift+C in Name / Amount / Ref'
                                iconColor='secondary.main'
                                sx={{
                                  p: 0.5,
                                  "&:hover": {
                                    backgroundColor: (theme) =>
                                      alpha(theme.palette.secondary.main, 0.1),
                                  },
                                }}
                              />
                              {canEditDetailPL && (
                                <StyledIconButton
                                  onClick={() =>
                                    handleRowRemove(row, section, index)
                                  }
                                  icon={DeleteIcon}
                                  tooltip='Remove Row'
                                  iconColor='error.main'
                                  sx={{
                                    p: 0.5,
                                    "&:hover": {
                                      backgroundColor: (theme) =>
                                        alpha(theme.palette.error.main, 0.1),
                                    },
                                  }}
                                />
                              )}
                              {showVertMenu && (
                                <StyledIconButton
                                  onClick={(event) =>
                                    handleActionMenuOpen(
                                      event,
                                      row,
                                      section,
                                      index
                                    )
                                  }
                                  icon={MoreVertIcon}
                                  tooltip='More Actions'
                                  iconColor='text.secondary'
                                  sx={{
                                    p: 0.5,
                                    "&:hover": {
                                      backgroundColor: (theme) =>
                                        alpha(
                                          theme.palette.text.secondary,
                                          0.1
                                        ),
                                    },
                                  }}
                                />
                              )}
                            </Stack>
                          );
                        },
                      },
                    ]}
                  />

                  {canEditDetailPL && (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: 1.5,
                        px: bottomActionBarConfig.px,
                        py: bottomActionBarConfig.py,
                        borderTop: "1px solid",
                        borderColor: (theme) =>
                          alpha(theme.palette.divider, 0.1),
                        bgcolor: "background.paper",
                        position: "sticky",
                        bottom: 0,
                        zIndex: 3,
                      }}
                    >
                      {useCompactTable ? (
                        <Stack
                          direction='row'
                          spacing={1}
                          alignItems='center'
                          sx={{ minWidth: 0 }}
                        >
                          <Box
                            sx={{
                              border: "1px solid",
                              borderColor: "divider",
                              borderRadius: 1,
                              px: 1,
                              py: 0.5,
                              bgcolor: (theme) =>
                                alpha(theme.palette.primary.main, 0.04),
                              flexShrink: 0,
                            }}
                          >
                            <Typography
                              variant='caption'
                              sx={{ fontWeight: 600, whiteSpace: "nowrap" }}
                            >
                              {selectedRows[section]?.length || 0} selected
                            </Typography>
                          </Box>
                          <Button
                            disabled={
                              (selectedRows[section]?.length || 0) === 0
                            }
                            endIcon={<MoreVertIcon />}
                            onClick={(event) =>
                              handleSelectedActionMenuOpen(event, section)
                            }
                            size='small'
                            variant='outlined'
                            sx={{
                              borderRadius: 1,
                              textTransform: "none",
                              whiteSpace: "nowrap",
                            }}
                          >
                            More actions
                          </Button>
                        </Stack>
                      ) : (
                        <Stack
                          direction='row'
                          spacing={1.25}
                          flexWrap='wrap'
                          useFlexGap
                        >
                          <Button
                            onClick={() => copySelectedRowsToClipboard(section)}
                            variant='outlined'
                            color='secondary'
                            startIcon={<AssignmentIcon />}
                            disabled={selectedRows[section].length === 0}
                            sx={{
                              borderColor: "secondary.main",
                              color: "secondary.main",
                              borderRadius: 2,
                              textTransform: "none",
                              boxShadow: "none",
                              "&:hover": {
                                bgcolor: (theme) =>
                                  alpha(theme.palette.secondary.main, 0.1),
                                borderColor: "secondary.main",
                                color: "secondary.main",
                                boxShadow: "none",
                              },
                            }}
                          >
                            Copy Selected ({selectedRows[section].length})
                          </Button>
                          <Tooltip
                            title={
                              selectedRows[section].length < 2
                                ? "Select at least two rows to merge"
                                : !areSelectedRowsMergeable(section)
                                  ? "Only rows with None (no Tax Adjustment) can be merged"
                                  : ""
                            }
                          >
                            <span>
                              <Button
                                onClick={() => handleMergeSelectedRows(section)}
                                variant='contained'
                                color='secondary'
                                disabled={
                                  selectedRows[section].length < 2 ||
                                  !areSelectedRowsMergeable(section)
                                }
                                startIcon={<CompareArrowsIcon />}
                                sx={{
                                  borderRadius: 2,
                                  textTransform: "none",
                                  boxShadow: "none",
                                  "&:hover": {
                                    boxShadow: "none",
                                  },
                                }}
                              >
                                Merge Selected
                              </Button>
                            </span>
                          </Tooltip>
                          <Button
                            onClick={() =>
                              setRemoveSelectedDialog({ open: true, section })
                            }
                            variant='contained'
                            color='error'
                            startIcon={<DeleteIcon />}
                            disabled={selectedRows[section].length === 0}
                            sx={{
                              borderRadius: 2,
                              textTransform: "none",
                              boxShadow: "none",
                              background: "red",
                              "&:hover": {
                                boxShadow: "none",
                              },
                            }}
                          >
                            Remove Selected ({selectedRows[section].length})
                          </Button>
                        </Stack>
                      )}

                      <Button
                        onClick={() => handleAddRow(section)}
                        variant='outlined'
                        color='primary'
                        startIcon={<AddCircleOutlineIcon />}
                        size={useCompactTable ? "small" : "medium"}
                        sx={{
                          borderRadius: useCompactTable ? 1 : 2,
                          flexShrink: 0,
                          textTransform: "none",
                          whiteSpace: "nowrap",
                        }}
                      >
                        Add Row
                      </Button>
                    </Box>
                  )}
                </>
              ) : (
                <Paper
                  elevation={0}
                  sx={{
                    p: emptyStateConfig.p,
                    textAlign: emptyStateConfig.inline ? "left" : "center",
                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.02),
                    border: "1px dashed",
                    borderColor: (theme) =>
                      alpha(theme.palette.primary.main, 0.2),
                    borderRadius: 2,
                    display: emptyStateConfig.inline ? "flex" : "block",
                    alignItems: emptyStateConfig.inline ? "center" : undefined,
                    justifyContent: emptyStateConfig.inline
                      ? "space-between"
                      : undefined,
                    gap: 1.5,
                  }}
                >
                  <Stack
                    direction={emptyStateConfig.inline ? "row" : "column"}
                    alignItems='center'
                    spacing={emptyStateConfig.inline ? 1 : 0}
                    sx={{ minWidth: 0 }}
                  >
                    <ReceiptLongIcon
                      sx={{
                        fontSize: emptyStateConfig.iconSize,
                        color: (theme) =>
                          alpha(theme.palette.primary.main, 0.3),
                        flexShrink: 0,
                        mb: emptyStateConfig.inline ? 0 : 2,
                      }}
                    />
                    <Typography
                      variant={emptyStateConfig.inline ? "body2" : "body1"}
                      color='text.secondary'
                      gutterBottom={!emptyStateConfig.inline}
                      noWrap={emptyStateConfig.inline}
                    >
                      No rows available in this section
                    </Typography>
                  </Stack>
                  {canEditDetailPL && (
                    <Button
                      onClick={() => handleAddRow(section)}
                      variant='outlined'
                      color='primary'
                      size={emptyStateConfig.inline ? "small" : "medium"}
                      startIcon={<AddCircleOutlineIcon />}
                      sx={{
                        mt: emptyStateConfig.inline ? 0 : 2,
                        borderRadius: emptyStateConfig.inline ? 1 : 2,
                        flexShrink: 0,
                        textTransform: "none",
                        whiteSpace: "nowrap",
                      }}
                    >
                      Add Row
                    </Button>
                  )}
                </Paper>
              )}
            </Box>
          </Collapse>
        </Paper>
      ))}

      <Menu
        anchorEl={selectedActionMenu.anchorEl}
        open={Boolean(selectedActionMenu.anchorEl)}
        onClose={handleSelectedActionMenuClose}
        sx={{
          mt: 1,
          "& .MuiPaper-root": {
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
            minWidth: 260,
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
          },
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 1.5,
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Typography variant='subtitle2' sx={{ fontWeight: 600, mb: 0.5 }}>
            Selected Rows
          </Typography>
          <Typography variant='caption' color='text.secondary'>
            {selectedActionCount} selected
          </Typography>
        </Box>
        <MenuItem
          disabled={selectedActionCount === 0}
          onClick={() => handleSelectedActionClick("copy")}
        >
          <ListItemIcon>
            <AssignmentIcon fontSize='small' color='secondary' />
          </ListItemIcon>
          <ListItemText
            primary='Copy Selected'
            secondary='Copy names and amounts'
          />
        </MenuItem>
        <MenuItem
          disabled={selectedActionMergeDisabled}
          onClick={() => handleSelectedActionClick("merge")}
        >
          <ListItemIcon>
            <CompareArrowsIcon fontSize='small' color='secondary' />
          </ListItemIcon>
          <ListItemText
            primary='Merge Selected'
            secondary='Only rows with no tax adjustment'
          />
        </MenuItem>
        <Divider />
        <MenuItem
          disabled={selectedActionCount === 0}
          onClick={() => handleSelectedActionClick("remove")}
        >
          <ListItemIcon>
            <DeleteIcon fontSize='small' color='error' />
          </ListItemIcon>
          <ListItemText
            primary='Remove Selected'
            secondary='Remove all selected rows'
          />
        </MenuItem>
      </Menu>

      <RemoveTaggingDialog
        open={adjustmentDialog.remove}
        onClose={() =>
          setAdjustmentDialog((prev) => ({ ...prev, remove: false }))
        }
        row={adjustmentDialog.row}
        source='p_l'
        onDone={async (row) => {
          try {
            const { section, index } = adjustmentDialog;
            const updatedRows = removeRow(section, index);

            if (row?.schedule) {
              await handleSave(
                "Successfully removed the row and adjustment.",
                false,
                {
                  section,
                  updatedRows,
                }
              );
            } else {
              handleSuccess("Successfully removed the row.");
            }
          } catch (error) {
            handleError("Failed to save the tax adjustment. Please try again.");
          }
        }}
        handleError={handleError}
      />

      <ChangeTaggingDialog
        open={adjustmentDialog.tagging}
        row={adjustmentDialog.row}
        source='p_l'
        from={adjustmentDialog.row?.taxAdjustment}
        to={adjustmentDialog.value}
        onClose={() =>
          setAdjustmentDialog((prev) => ({ ...prev, tagging: false }))
        }
        onDone={async (updatedRow) => {
          try {
            const { section, row } = adjustmentDialog;
            const updatedRows = getSectionData(section).map((currentRow) => {
              if (currentRow.row_id !== (updatedRow.row_id || row?.row_id)) {
                return currentRow;
              }
              const nextRow = {
                ...currentRow,
                taxAdjustment: updatedRow.taxAdjustment,
              };
              if (updatedRow.schedule) {
                nextRow.schedule = updatedRow.schedule;
              } else {
                delete nextRow.schedule;
              }
              return nextRow;
            });
            updateSectionRows(section, updatedRows);
            setAdjustmentDialog((prev) => ({ ...prev, tagging: false }));
            await handleSave("Successfully update the tax adjustment.", true, {
              section,
              updatedRows,
            });
          } catch (error) {
            handleError(
              "Failed to save the tax adjustment. No changes were applied. Please try again."
            );
          }
        }}
        handleError={handleError}
      />

      {/* Remove Selected Confirmation Dialog */}
      <CustomDialog
        open={removeSelectedDialog.open}
        onClose={() => setRemoveSelectedDialog({ open: false, section: null })}
        onConfirm={() => handleRemoveSelectedRows(removeSelectedDialog.section)}
        loading={removeSelectedLoading}
        title={`Remove Selected Rows?`}
        description={`Are you sure you want to remove all selected rows in ${removeSelectedDialog.section}? This action cannot be undone.`}
        type='error'
      />

      {/* Merge Selected Confirmation Dialog */}
      <CustomDialog
        open={mergeDialog.open}
        onClose={() => setMergeDialog({ open: false, section: null })}
        onConfirm={handleConfirmMerge}
        title='Merge Selected Rows?'
        description={`Are you sure you want to merge the selected rows in ${mergeDialog.section}? This action cannot be undone.`}
        type='info'
      />

      <ScheduleTaggingDialog
        companyType={company.companyType}
        open={taggingDialog.open}
        editMode={taggingDialog.isEdit}
        tcId={tcId}
        detailsId={detailsId}
        row={taggingDialog.row}
        rowIndex={taggingDialog.index}
        section={taggingDialog.section}
        reference='P&L'
        source='p_l'
        onDone={onDoneTagging}
        onClose={onCloseTaggingDialog}
        handleError={handleError}
      />

      {/* Action Menu */}
      <ActionMenu
        anchorEl={actionMenu.anchorEl}
        open={Boolean(actionMenu.anchorEl)}
        onClose={handleActionMenuClose}
        row={actionMenu.row}
        onActionClick={handleActionClick}
        canEditDetailPL={canEditDetailPL}
      />

      {/* Copy Snackbar */}
      <Snackbar
        open={copySnackbar.open}
        autoHideDuration={2000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity='success'
          variant='filled'
          sx={{ width: "100%" }}
        >
          {copySnackbar.message}
        </Alert>
      </Snackbar>
    </Stack>
  );
};

export default Details;
